<template>
  <div v-if="settings?.title" class="text-xl font-bold mb-2 px-1">{{ settings?.title }}</div>
  <div class="grid grid-cols-1 gap-2">
    <div class="grid grid-cols-1 gap-2">
      <div class="flex flex-col lg:flex-row gap-2">
        <el-select
            v-if="!settings?.hideFilter"
            clearable
            v-model="selectedLocation"
            :placeholder="t('selectStore')"
            value-key="id"
            @change="onSelectedLocationChanged">
          <el-option
              v-for="option in locations"
              :key="option.id"
              :label="$ctx.locale == 'en'? option.enName: option.arName"
              :value="option"
          ></el-option>
        </el-select>
        <el-select
          v-if="!settings?.hideFilter"
          clearable
          v-model="selectedPeriod"
          :placeholder="t('selectPeriod')"
          @change="onSelectedPeriodChanged"
        >
          <el-option key="today" :label="t('today')" value="today"></el-option>
          <el-option key="yesterday" :label="t('yesterday')" value="yesterday"></el-option>
          <el-option key="this_week" :label="t('thisWeek')" value="this_week"></el-option>
          <el-option key="last_week" :label="t('lastWeek')" value="last_week"></el-option>
          <el-option key="this_month" :label="t('thisMonth')" value="this_month"></el-option>
          <el-option key="last_month" :label="t('lastMonth')" value="last_month"></el-option>
        </el-select>
        <div v-if="!settings?.hideFilter">
          <el-date-picker
            v-model="range"
            class="w-full"
            type="daterange"
            range-separator="to"
            :start-placeholder="t('startDate')"
            :end-placeholder="t('endDate')"
            @change="onFilterChange"
            :format="settings.dateRangeFormat || 'DD-MM-YYYY'"
            :disabledDate="settings.limitDateRangeTo30Days ? disabledDate : undefined"
          >
          </el-date-picker>
        </div>
        <slot name="filter"></slot>

        <div v-if="!settings.hideExport">
          <el-button class="w-full btn" plain @click="exportTable">{{ t('export') }}</el-button>
        </div>
      </div>
      <slot name="filter-bottom"></slot>
    </div>

    <slot name="cards" :data="data"></slot>
    <slot name="chart" :data="data"></slot>

    <ag-grid-vue
      v-if="!settings.hideTable"
      v-bind:class="settings.theme || 'ag-theme-quartz'"
      :autoSizeStrategy="autoSizeStrategy"
      :columnDefs="columnDefs"
      :rowData="rowData"
      rowSelection="multiple"
      :enable-rtl="$ctx.getDir() === 'rtl'"
      style="height: 85vh"
      :pagination="true"
      :paginationAutoPageSize="true"
      :defaultColDef="defaultColDef"
      :onGridReady="onGridReady"
    >
    </ag-grid-vue>
  </div>
</template>
<script lang="ts" setup>
import { useHTTP } from '@/plugins/http'
import { ColDef, GridApi } from 'ag-grid-community'
import { AgGridVue } from 'ag-grid-vue3'
import dayjs from 'dayjs'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import Location from '@/models/company/Location'
import { ReportsTableSettings } from './utls'
import { ElMessage } from 'element-plus'
import { useContext } from '@/plugins/context'

const props = defineProps({
  columns: { type: Array<any> },
  endpoint: { type: String },
  settings: {
    type: Object as () => ReportsTableSettings,
    default: () => ({
      hideTable: false,
      hideCards: false,
      hideChart: false,
      hideExport: false,
      customRowsKey: null,
      tableType: null,
      theme: null,
      dateRangeFormat: null,
      appendQueryParams: null,
      selectedPeriod: null,
      limitDateRangeTo30Days: false,
      hideFilter: false,
    })
  }
})
const emits = defineEmits('onDataLoaded')
const http = useHTTP()
const { t } = useI18n({
  messages: {
    en: {
      selectStore: 'Select Store',
      selectPeriod: 'Select Period',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This Week',
      lastWeek: 'Last Week',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      export: 'Export',
      dateRangeLimitedTo30Days: 'Date range limited to 30 days maximum'
    },
    ar: {
      selectStore: 'اختر المتجر',
      selectPeriod: 'اختر المدة',
      today: 'اليوم',
      yesterday: 'البارحة',
      thisWeek: 'هذا الاسبوع',
      lastWeek: 'الاسبوع الماضي',
      thisMonth: 'هذا الشهر',
      lastMonth: 'الشهر الماضي',
      export: 'تصدير',
      dateRangeLimitedTo30Days: 'المدة المحددة يجب ان لا تزيد عن 30 يوم'
    }
  }
})

const autoSizeStrategy = ref({
  type: 'fitCellContents'
})

const payload = ref({
  location_id: null,
  period: null,
  from_date: null,
  to_date: null,
  filter: null
})

const data = ref<any>()
const columnDefs = ref<ColDef[]>([])
const rowData = ref<any[]>([])
const defaultColDef = ref<ColDef>({})
const searchTerm = ref('')
const selectedValue = ref('')
const gridApi = ref<GridApi | undefined>(undefined)
const range = ref<Date[]>([])
const selectedPeriod = ref<any>()


const locations = ref<Array<Location>>()
const selectedLocation = ref<any>()
const ctx = useContext();

// Store original document title to restore on unmount
const originalTitle = ref<string>(document.title)

onMounted(async () => {
  // Set document title for PDF download (Ctrl+P)
  if (props.settings?.title) {
    document.title = props.settings.title + ' - Thecky'
  }

  http.get<Array<Location>>(Location.ENDPOINT).then(data => {
    locations.value = data.map(i => Location.from(i))

    // Auto-select current location if setting is enabled
    if (props.settings?.autoSelectLocation) {
      if (ctx?.currentLocation?.id && locations.value.length > 0) {
        const currentLoc = locations.value.find(loc => loc.id === ctx.currentLocation.id)
        if (currentLoc) {
          selectedLocation.value = currentLoc
          payload.value.location_id = ctx.currentLocation.id
          navigate(payload.value)
        }
      }
    }
  })

  if (props.settings.selectedPeriod) {
    selectedPeriod.value = props.settings.selectedPeriod;
    onSelectedPeriodChanged(selectedPeriod.value)
  }

  if (props.settings.hideTable) {
    return;
  }

  defaultColDef.value = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 150
  }

  columnDefs.value = props.columns
})

// Restore original document title when component unmounts
onBeforeUnmount(() => {
  document.title = originalTitle.value
})

// Methods
async function navigate(filter: any) {
  gridApi.value?.showLoadingOverlay()

  const urlParams = new URLSearchParams()
  Object.keys(props.settings.appendQueryParams || {}).forEach((k) => {
    if (props.settings.appendQueryParams[k] != null) {
      urlParams.set(k, props.settings.appendQueryParams[k])
    }
  })

  Object.keys(filter).forEach((k) => {
    if (filter[k] != null) {
      urlParams.set(k, filter[k])
    }
  })


  try {
    const resp = await http.get<any[]>(props.endpoint + `?${urlParams.toString()}`)
    data.value = resp
    emits('onDataLoaded', resp)

    if (props.settings?.customRowsKey) {
      rowData.value = resp[props.settings?.customRowsKey]
    } else {
      rowData.value = resp
    }
  } catch (error) {
    console.log(error)
  }

  gridApi.value?.hideOverlay()
}

async function updateRowData() {
  navigate(payload.value)
}

async function onGridReady(params: any) {
  gridApi.value = params.api
  gridApi.value?.sizeColumnsToFit()
  await updateRowData()
}

async function onFilterChange() {
  if (range.value && range.value.length == 2) {
    // Enforce 30-day limit if enabled
    if (props.settings.limitDateRangeTo30Days) {
      const start = new Date(range.value[0])
      const end = new Date(range.value[1])
      const diffTime = Math.abs(end.getTime() - start.getTime())
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays > 30) {
        // If more than 30 days, adjust the end date
        const newEnd = new Date(start)
        newEnd.setDate(newEnd.getDate() + 30)
        range.value = [start, newEnd]
        
        // Show message to user
        ElMessage({
          message: t('dateRangeLimitedTo30Days'),
          type: 'warning',
        })
      }
    }
    
    selectedPeriod.value = null
    payload.value.period = 'range'
    payload.value.from_date = dayjs(range.value[0]).format('YYYY-MM-DD')
    payload.value.to_date = dayjs(range.value[1]).format('YYYY-MM-DD')
  } else {
    payload.value.period = null
    payload.value.from_date = null
    payload.value.to_date = null
  }

  if (props.settings.periodRequired) {
    if (!payload.value.period) {
      return;
    }
  }

  navigate(payload.value)
}

async function onSelectedPeriodChanged(event: any) {
  range.value = []

  payload.value.period = event
  payload.value.from_date = null
  payload.value.to_date = null

  if (props.settings.filterRequired) {
    if (!payload.value.filter) {
      return;
    }
  }

  navigate(payload.value)
}

async function onSelectedLocationChanged(event: any) {
  payload.value.location_id = event.id
  if (props.settings.filterRequired) {
   if (!payload.value.filter) {
     return;
   }
  }

  if (props.settings.periodRequired) {
    if (!payload.value.period) {
      return;
    }
  }

  navigate(payload.value)
}

function exportTable() {
  const today = dayjs().format('YYYY-MM-DD')
  const reportName = props.settings?.title || 'report'
  const fileName = `${reportName}_${today}.csv`

  gridApi.value?.exportDataAsCsv({
    onlySelected: false,
    fileName: fileName
  })
}

const disabledDate = (time: Date) => {
  if (!range.value || range.value.length === 0) {
    return false
  }

  const start = range.value[0]
  const end = range.value[1]
  
  // If both dates are selected, don't apply additional restrictions
  if (start && end) {
    return false
  }
  
  if (start && !end) {
    // If start date is selected, limit end date to 3 days after
    const maxDate = new Date(start)
    maxDate.setDate(maxDate.getDate() + 3)
    return time.getTime() > maxDate.getTime() || time.getTime() < start.getTime()
  } else if (!start && end) {
    // If end date is selected, limit start date to 3 days before
    const minDate = new Date(end)
    minDate.setDate(minDate.getDate() - 3)
    return time.getTime() < minDate.getTime() || time.getTime() > end.getTime()
  }
  
  return false
}

defineExpose({
  payload,
  navigate,
  data,
  selectedPeriod,
  selectedLocation
})
</script>
<style></style>
